<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.every24HoursReport.title') }}
        </h4>
        <p class="ml-5 pb-1 mb-1 align-self-end operation-office-initial-announce"
          v-show="!savedFormValues && operationOfficeInitialMessage && beforeSearchParams == null">
          {{ i18n.global.t('pages.common.list.initialMessage') }}
        </p>
      </div>
      <div>
        <List
          class="mb-2"
          :pageInfo="pageInfo"
          :searched="searched"
          :items="dailyReportList"
          :listLoading="listLoading"
          :notReady="notReady"
          :isOperationOffice="isOperationOffice"
          :isDepartment="isDepartment"
          @select="handleSelectDeliveryPlanId"
          @changeItem="searchDailyReportList"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
        <hr />
      </div>
      <div>
        <ButtonArea
          v-if="!!selectedDeliveryPlanIds.length"
          :operationOfficeId="form.operationOffice?.id"
          :deliveryPlanIds="selectedDeliveryPlanIds"
        />
      </div>
    </div>
    <SearchForm
      :loading="listLoading"
      @search="searchDailyReportList"
      :isOperationOffice="isOperationOffice"
      :isSubManagementOffice="isSubManagementOffice"
      :isManagementOffice="isManagementOffice"
      :isDepartment="isDepartment"
      :defaultBackDays="defaultBackDays"
      @clear="clearEvery24hoursReportSearchParams"
    />
  </div>
</template>

<script>
  import { MANAGEMENT_OFFICE_TYPE, SUB_MANAGEMENT_OFFICE_TYPE, OPERATION_OFFICE_TYPE, DEPARTMENT_OFFICE_TYPE } from "@/services/consts.ts"
  import SearchForm from "@/components/Every24HoursReportPreviewParts/SearchForm.vue";
  import List from "@/components/Every24hoursReportList"
  import ButtonArea from "@/components/Every24HoursReportPreviewParts/Every24hoursPreviewButtonArea"
  import Api from "@/services/api/ApiServiceFabrick";
  import PaginateWithCount from "@/components/PaginateWithCount.vue"
  import { useAuth } from "@/services/user/Auth0UserProvider"
  import { formatDate } from "@/services/functions"
  import i18n from "@/lang/i18n";
  import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";
  import { getSelfOffice } from "@/services/functions/operationOffice";

  export default {
    props: {
      showFlash: {
        type: Function
      },
      switchContentLoading: {
        type: Function
      }
    },
    data() {
      return {
        form: {},
        currentPage: 1,
        totalPage: 0,
        itemsPerPage: 50,
        allElementsCount: 0,
        dailyReportList: [],
        selectedDeliveryPlanIds: [],
        listLoading: false,
        searched: false,
        beforeSearchParams: null,
        isOperationOffice: false,
        isSubManagementOffice: false,
        isManagementOffice: false,
        isDepartment: false,
        operationOfficeInitialMessage: false,
        notReady: true,
        defaultBackDays: 3,
        savedFormValues: loadDataFromSessionStorage('every24hoursReportSearchForm')
      }
    },
    setup() {
      document.title = i18n.global.t('pages.every24HoursReport.title');
      const { loginWithRedirect } = useAuth();
      return {
        loginWithRedirect,
        i18n
      }
    },
    mounted() {
      this.switchContentLoading(true)
      Api().getSelfAccount().then((data) => {
        this.isManagementOffice = data.login_organization_type == MANAGEMENT_OFFICE_TYPE
        this.isSubManagementOffice = data.login_organization_type == SUB_MANAGEMENT_OFFICE_TYPE
        this.isOperationOffice = data.login_organization_type == OPERATION_OFFICE_TYPE
        this.isDepartment = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
        this.switchContentLoading(false)
        const savedSearchParams = loadDataFromSessionStorage('every24hoursReportSearchParams')
        if (savedSearchParams) {
          savedSearchParams.start_date = new Date(savedSearchParams.start_date)
          savedSearchParams.end_date = new Date(savedSearchParams.end_date)
          return this.searchDailyReportList(savedSearchParams)
        } else if (this.isOperationOffice || this.isDepartment) {
          return this.getInitialEvery24hoursReport()
        }
        this.notReady = false
      })
    },
    watch: {
      currentPage: {
        handler() {
          this.getDailyReports(this.createRequest(this.currentPage, false))
        }
      }
    },
    methods: {
      async getInitialEvery24hoursReport() {
        this.listLoading = true
        let selfDepartment
        let selfOperationOffice
        if (this.isDepartment) {
          selfDepartment = await this.getDepartment()
        }
        selfOperationOffice = await getSelfOffice()
        this.form.operationOffice = {
          id: selfOperationOffice.id
        }
        const request = this.makeInitialParams(selfOperationOffice, selfDepartment)
        this.apiGetDailyReports(request)
      },
      async getDepartment() {
        let department
        await Api().autoCompleteDepartments().then((data) => {
          department = data
        })
        return department
      },
      makeInitialParams(operationoffice, departments = []) {
        let page = 1
        let dateRange =  [
          new Date(),
          new Date()
        ]
        dateRange[0].setDate(dateRange[0].getDate() - this.defaultBackDays)
        const department = departments.length == 1 ? departments[0] : null

        const ret = {
          page: page,
          operation_office_id: operationoffice?.id,
          management_office_id: operationoffice?.management_officeable_id,
          management_officeable_type: operationoffice?.management_officeable_type,
          department_id: department?.id,
          daterange: this.convertDateTimeTodate(dateRange[0]) + " - " + this.convertDateTimeTodate(dateRange[1]),
          authority_name: 'every24hoursView'
        }
        ret["delivery_date"] = 1
        return ret
      },
      handleSelectDeliveryPlanId(selectedDeliveryPlanIds) {
        this.selectedDeliveryPlanIds = selectedDeliveryPlanIds
      },
      searchDailyReportList(form) {
        this.operationOfficeInitialMessage = false
        this.searched = true
        this.form = form
        const request = this.createRequest()
        this.getDailyReports(request)
        saveDataToSessionStorage('every24hoursReportSearchParams', form)
      },
      createRequest(page = 1, updateOption = true) {
        let req
        if (updateOption || !this.beforeSearchParams) {
          req = this.makeSearchParams(page)
          this.beforeSearchParams = req
          return req
        } else {
          req = this.beforeSearchParams
          req.page = page
          return req
        }
      },
      getDailyReports(req) {
        this.listLoading = true
        this.apiGetDailyReports(req)
      },
      apiGetDailyReports(req) {
        Api().getDailyReports(req).then((data) => {
          this.dailyReportList = data.items
          this.currentPage = data.current_page
          this.totalPage = data.total_page
          this.allElementsCount = data.total
          this.itemsPerPage = data.per_page
          this.listLoading = false
          this.operationOfficeInitialMessage = true
        }).catch((error) => {
          const res = error.response
          switch (res.status) {
            case 401:
              this.loginWithRedirect({ appState: { targetUrl: location.pathname } })
              break
            case 403:
              location.reload()
              break
            case 422:
              this.showFlash(i18n.global.t('pages.every24HoursReport.errors.getDailyReport'), "error", false)
          }
          this.listLoading = false
        })
      },
      makeSearchParams(page){
        const startDate = this.convertDateTimeTodate(this.form?.dateRange?.[0])
        const endDate = this.convertDateTimeTodate(this.form?.dateRange?.[1])
        const ret = {
          page: page,
          operation_office_id: this.form?.operationOffice?.id,
          management_office_id: this.form?.managementOffice?.id,
          management_officeable_type: this.form?.managementOffice?.type,
          group_id: this.form?.group?.id,
          department_id: this.form?.department?.id,
          car_id: this.form?.vehicle?.id,
          driver_id: this.form?.driver?.driver_id,
          account_id: this.form?.driver?.id,
          daterange: startDate + " - " + endDate,
          authority_name: 'every24hoursView'
        }
        const key = this.form.selectDateType == "returning" ? "return_date" : "delivery_date"
        ret[key] = 1
        return ret
      },
      convertDateTimeTodate(datetime) {
        if (!datetime) {
          return ''
        }
        const date = datetime instanceof Date ? datetime : new Date(datetime)
        if (Number.isNaN(date.getTime())) {
          return ''
        }
        return formatDate(date, '/')
      },
      clearEvery24hoursReportSearchParams() {
        clearSessionStorage('every24hoursReportSearchParams')
      }
    },
    components: {
      SearchForm,
      List,
      ButtonArea,
      PaginateWithCount,
    }
  }
</script>

<style scoped>
.content {
  min-width: 58vw;
}
</style>
<style>
.main-panel {
  height: initial !important;
  max-height: initial !important;
}
.exec-btn {
  background-color: rgb(21, 130, 156);
  color: white !important;
}
.reset-btn {
  background-color: rgb(245, 246, 247);
  color: rgb(21, 130, 156) !important;
  font-weight: bold;
}
.operation-office-initial-announce {
  font-size: 10px
}
</style>
